import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { KioskInfo, KioskService } from './apiKiosk';
import { KioskBrandHeader } from './KioskBrandHeader';

/**
 * Confirmation step shown after a walk-in picks a service at the kiosk. It
 * echoes the chosen service back so the visitor can confirm it, collects an
 * optional name and phone, and only issues the token when they submit.
 */
export function KioskServiceForm({
  info,
  service,
  busy,
  onBack,
  onSubmit,
}: {
  info: KioskInfo;
  service: KioskService;
  busy: boolean;
  onBack: () => void;
  onSubmit: (customerName: string, customerPhone: string) => void;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (busy) return;
    onSubmit(name.trim(), phone.trim());
  };

  return (
    <main className="flex min-h-screen flex-col items-center bg-primary px-4 py-10 text-primary-foreground">
      <KioskBrandHeader info={info} />
      <form
        onSubmit={submit}
        className="mt-8 w-full max-w-md rounded-3xl bg-card p-8 text-foreground shadow-2xl"
      >
        <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground">
          {t('kiosk.selected_service')}
        </p>
        <p className="mt-1 text-2xl font-semibold text-primary">
          {service.name}
        </p>
        {service.name_ar ? (
          <p
            dir="rtl"
            lang="ar"
            className="text-lg font-semibold text-primary/80"
          >
            {service.name_ar}
          </p>
        ) : null}

        <label className="mt-6 block space-y-1.5 text-sm font-medium">
          <span>{t('kiosk.customer_name')}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            className="h-12 w-full rounded-xl border border-input bg-background px-4 text-base text-foreground"
          />
        </label>
        <label className="mt-4 block space-y-1.5 text-sm font-medium">
          <span>{t('kiosk.customer_phone')}</span>
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            className="h-12 w-full rounded-xl border border-input bg-background px-4 text-base text-foreground"
          />
        </label>

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            className="h-14 flex-1 rounded-xl border border-input text-base font-semibold text-foreground transition hover:bg-muted disabled:opacity-60"
          >
            {t('kiosk.back')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="h-14 flex-[2] rounded-xl bg-primary text-base font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
          >
            {busy ? t('kiosk.loading') : t('kiosk.submit')}
          </button>
        </div>
      </form>
    </main>
  );
}
